/**
 * Cart Drawer — Web Component for Empire theme
 * Intercepts ATC requests, slides open a cart drawer panel.
 */
(function () {
  'use strict';

  const SELECTORS = {
    drawer: '#cart-drawer',
    overlay: '[data-cart-drawer-overlay]',
    close: '[data-cart-drawer-close]',
    body: '[data-cart-drawer-body]',
    items: '[data-cart-drawer-items]',
    empty: '[data-cart-drawer-empty]',
    footer: '[data-cart-drawer-footer]',
    count: '[data-cart-drawer-count]',
    subtotal: '[data-cart-drawer-subtotal]',
    qtyMinus: '[data-qty-minus]',
    qtyPlus: '[data-qty-plus]',
    qtyValue: '[data-qty-value]',
    removeItem: '[data-remove-item]',
    cartItem: '[data-cart-item]'
  };

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._busy = false;
    }

    connectedCallback() {
      this._bindStaticEvents();
      this._interceptFetch();
      this._interceptXHR();
    }

    /* ── Static event listeners ─────────────────────── */

    _bindStaticEvents() {
      // Close button(s)
      this.querySelectorAll(SELECTORS.close).forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      // Overlay click
      const overlay = this.querySelector(SELECTORS.overlay);
      if (overlay) overlay.addEventListener('click', () => this.close());

      // Escape key
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && this._open) this.close();
      });

      // Delegate quantity & remove clicks
      this.addEventListener('click', e => {
        const minus = e.target.closest(SELECTORS.qtyMinus);
        const plus = e.target.closest(SELECTORS.qtyPlus);
        const remove = e.target.closest(SELECTORS.removeItem);

        if (minus) this._changeQty(minus, -1);
        else if (plus) this._changeQty(plus, 1);
        else if (remove) this._removeItem(remove);
      });

      // Listen for cart icon clicks in the header
      document.querySelectorAll('a[href="/cart"], [data-header-cart]').forEach(el => {
        el.addEventListener('click', e => {
          e.preventDefault();
          this.open();
        });
      });
    }

    /* ── Fetch intercept (catches Empire's ATC AJAX) ── */

    _interceptFetch() {
      const originalFetch = window.fetch;
      const drawer = this;

      window.fetch = function () {
        const url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
        const isCartAdd = url.includes('/cart/add');

        return originalFetch.apply(this, arguments).then(response => {
          if (isCartAdd && response.ok) {
            // Clone so the original consumer can still read it
            const cloned = response.clone();
            cloned.json().then(() => {
              drawer._hideATCBanner();
              drawer.open();
            }).catch(() => {});
          }
          return response;
        });
      };
    }

    /* ── XHR intercept (fallback for older ATC code) ── */

    _interceptXHR() {
      const drawer = this;
      const origOpen = XMLHttpRequest.prototype.open;
      const origSend = XMLHttpRequest.prototype.send;

      XMLHttpRequest.prototype.open = function (method, url) {
        this._cdUrl = url;
        return origOpen.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function () {
        if (this._cdUrl && this._cdUrl.includes('/cart/add')) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300) {
              drawer._hideATCBanner();
              drawer.open();
            }
          });
        }
        return origSend.apply(this, arguments);
      };
    }

    /* ── Hide Empire's default ATC notification banner ─ */

    _hideATCBanner() {
      const banner = document.querySelector('[data-atc-banner]');
      if (banner) {
        banner.style.display = 'none';
        banner.classList.remove('is-active');
      }
    }

    /* ── Open / Close ──────────────────────────────── */

    open() {
      this._refreshCart().then(() => {
        this.classList.add('is-open');
        document.body.classList.add('cart-drawer-open');
        this._open = true;
        // Focus the close button for accessibility
        const closeBtn = this.querySelector(SELECTORS.close);
        if (closeBtn) closeBtn.focus();
      });
    }

    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('cart-drawer-open');
      this._open = false;
    }

    /* ── Refresh cart data ─────────────────────────── */

    _refreshCart() {
      return fetch('/cart.js', { credentials: 'same-origin' })
        .then(r => r.json())
        .then(cart => this._render(cart))
        .catch(err => console.error('Cart drawer: fetch error', err));
    }

    /* ── Render cart contents ──────────────────────── */

    _render(cart) {
      const bodyEl = this.querySelector(SELECTORS.body);
      const footerEl = this.querySelector(SELECTORS.footer);
      const countEls = document.querySelectorAll(SELECTORS.count);
      const subtotalEl = this.querySelector(SELECTORS.subtotal);

      // Update header cart count (both in drawer and in site header)
      const countText = cart.item_count > 0 ? '(' + cart.item_count + ')' : '';
      countEls.forEach(el => { el.textContent = countText; });

      // Also update the header cart badge
      this._updateHeaderBadge(cart.item_count);

      if (cart.item_count === 0) {
        bodyEl.innerHTML = '<div class="cart-drawer__empty" data-cart-drawer-empty>' +
          '<p>Your cart is empty</p>' +
          '<button class="cart-drawer__continue" type="button" data-cart-drawer-close>Continue Shopping</button>' +
          '</div>';
        footerEl.style.display = 'none';

        // Re-bind close on the new continue shopping button
        bodyEl.querySelector('[data-cart-drawer-close]').addEventListener('click', () => this.close());
        return;
      }

      // Build items HTML
      let itemsHTML = '';
      cart.items.forEach(item => {
        const imgSrc = item.image ? this._resizeImg(item.image, 120) : '';
        const imgTag = imgSrc
          ? '<img src="' + imgSrc + '" alt="' + this._esc(item.title) + '" width="60" height="60" loading="lazy">'
          : '';

        const variantHTML = item.variant_title
          ? '<span class="cart-drawer-item__variant">' + this._esc(item.variant_title) + '</span>'
          : '';

        itemsHTML += '<div class="cart-drawer-item" data-cart-item data-line-key="' + item.key + '">' +
          '<a href="' + item.url + '" class="cart-drawer-item__image">' + imgTag + '</a>' +
          '<div class="cart-drawer-item__details">' +
            '<a href="' + item.url + '" class="cart-drawer-item__title">' + this._esc((function(x){x=String(x||'').split(' | ')[0];var i=x.lastIndexOf(' \u2013 ');var m=i>=0?x.slice(i+3).trim():x;return m.length<14?x:m;})(item.product_title)) + '</a>' /* cq nom court */ +
            variantHTML +
            '<div class="cart-drawer-item__bottom">' +
              '<div class="cart-drawer-item__qty">' +
                '<button class="cart-drawer-item__qty-btn" data-qty-minus aria-label="Decrease quantity">-</button>' +
                '<span class="cart-drawer-item__qty-value" data-qty-value>' + item.quantity + '</span>' +
                '<button class="cart-drawer-item__qty-btn" data-qty-plus aria-label="Increase quantity">+</button>' +
              '</div>' +
              '<span class="cart-drawer-item__price money">' + this._money(item.final_line_price) + '</span>' +
            '</div>' +
          '</div>' +
          '<button class="cart-drawer-item__remove" data-remove-item aria-label="Remove ' + this._esc(item.title) + '">' +
            '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M10.5 3.5L3.5 10.5M3.5 3.5l7 7"/></svg>' +
          '</button>' +
        '</div>';
      });

      bodyEl.innerHTML = '<div class="cart-drawer__items" data-cart-drawer-items>' + itemsHTML + '</div>';

      // Update footer + rebuild the whole summary block (discount tiers, strike, total)
      footerEl.style.display = '';
      const summaryEl = this.querySelector('[data-cart-drawer-summary]');
      if (summaryEl) {
        summaryEl.innerHTML = this._summaryHTML(cart);
      } else if (subtotalEl) {
        subtotalEl.innerHTML = this._money(cart.total_price);
      }
    }

    /* ── Build summary (Subtotal / Bundle deal / Total) ── */

    _summaryHTML(cart) {
      const orig = Number(cart.original_total_price) || 0;
      const total = Number(cart.total_price) || 0;
      const saving = orig - total;
      let html = '';
      if (saving > 0) {
        html += '<div class="cart-drawer__discount-row">' +
          '<span>Subtotal</span>' +
          '<span class="cart-drawer__strike money">' + this._money(orig) + '</span>' +
          '</div>';

        const apps = cart.cart_level_discount_applications || [];
        let label;
        if (apps.length > 0 && apps[0].title) {
          label = apps[0].title;
        } else {
          label = cart.item_count >= 3
            ? 'Bundle deal · 3+ scarves −20%'
            : 'Bundle deal · 2+ scarves −15%';
        }
        html += '<div class="cart-drawer__discount-row cart-drawer__discount-row--save">' +
          '<span>' + this._esc(label) + '</span>' +
          '<span>−' + this._money(saving) + '</span>' +
          '</div>';
      }
      html += '<div class="cart-drawer__subtotal">' +
        '<span>' + (saving > 0 ? 'Total' : 'Subtotal') + '</span>' +
        '<span class="cart-drawer__subtotal-value money" data-cart-drawer-subtotal>' +
        this._money(cart.total_price) + '</span>' +
        '</div>';
      return html;
    }

    /* ── Quantity change ───────────────────────────── */

    _changeQty(btn, delta) {
      if (this._busy) return;
      const item = btn.closest(SELECTORS.cartItem);
      if (!item) return;

      const key = item.getAttribute('data-line-key');
      const valueEl = item.querySelector(SELECTORS.qtyValue);
      const current = parseInt(valueEl.textContent, 10) || 1;
      const newQty = Math.max(0, current + delta);

      this._busy = true;
      item.style.opacity = '0.5';

      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty })
      })
        .then(r => r.json())
        .then(cart => {
          this._render(cart);
          this._busy = false;
        })
        .catch(() => {
          item.style.opacity = '1';
          this._busy = false;
        });
    }

    /* ── Remove item ───────────────────────────────── */

    _removeItem(btn) {
      if (this._busy) return;
      const item = btn.closest(SELECTORS.cartItem);
      if (!item) return;

      const key = item.getAttribute('data-line-key');
      this._busy = true;
      item.style.opacity = '0.5';

      fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      })
        .then(r => r.json())
        .then(cart => {
          this._render(cart);
          this._busy = false;
        })
        .catch(() => {
          item.style.opacity = '1';
          this._busy = false;
        });
    }

    /* ── Update site header cart badge ─────────────── */

    _updateHeaderBadge(count) {
      // Empire renders the number via CSS `content: attr(data-header-cart-count)`.
      // Setting textContent here would stack a 2nd number on top of it -> "3" + "1".
      // Update the ATTRIBUTE (and visibility class) instead, never textContent.
      document.querySelectorAll('[data-header-cart-count]').forEach(el => {
        el.textContent = '';
        el.setAttribute('data-header-cart-count', count > 0 ? count : '');
        el.classList.toggle('visible', count > 0);
      });
      // Plain text badges from other themes (no data-attr): safe to set text.
      document.querySelectorAll('.cart-count, .header__cart-count').forEach(el => {
        if (el.hasAttribute('data-header-cart-count')) return;
        el.textContent = count > 0 ? count : '';
        el.style.display = count > 0 ? '' : '';
      });
    }

    /* ── Helpers ────────────────────────────────────── */

    _resizeImg(url, size) {
      if (!url) return '';
      // Shopify image URL resizing
      if (url.includes('cdn.shopify.com')) {
        return url.replace(/(_\d+x\d+)?(\.(jpg|jpeg|png|gif|webp))/, '_' + size + 'x' + '$2');
      }
      return url;
    }

    _money(cents) {
    var n = Number(cents);
    if (!isFinite(n)) n = 0;
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur }).format(n / 100);
    } catch (e) {
      return '$' + (n / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }

    _esc(str) {
      var div = document.createElement('div');
      div.textContent = str || '';
      return div.innerHTML;
    }
  }

  // Register the custom element
  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }
})();
